import { useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Deep-link support for the Guest Map: when the guest arrives via
 * `?venue=<id>` OR `location.state.targetVenueId` (e.g. "My list" tap,
 * announcement notification), jump straight to that venue's expanded
 * card. Steps:
 *   1. Wait until `sortedVenues` actually contains the venue.
 *   2. If it's not in the list yet, pin it so it floats to the top.
 *   3. If it's not in `allVenues` either (closed or out-of-radius), hydrate
 *      it on-demand via `/api/venues/<id>` and then pin it.
 *   4. Once present, expand + scroll it into view.
 *   5. Clear the navigation state / query param so back/forward doesn't
 *      keep re-triggering the effect.
 */
export function useOpenTargetVenue({
  location,
  navigate,
  sortedVenues,
  allVenues,
  setAllVenues,
  setPinnedVenueId,
  setExpandedVenueId,
  venueListRef,
}) {
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || '');
    const targetVenueId = location.state?.targetVenueId || searchParams.get('venue');
    if (!targetVenueId) return;

    if (!sortedVenues.some((v) => v.id === targetVenueId)) {
      if (allVenues.some((v) => v.id === targetVenueId)) {
        setPinnedVenueId(targetVenueId);
      } else {
        // Hydrate the venue on demand (closed/out-of-radius venues aren't
        // in /api/venues). Same fallback the EventsCarousel uses.
        (async () => {
          try {
            const res = await axios.get(`${API}/venues/${targetVenueId}`);
            if (res.data) {
              setAllVenues((prev) => (prev.some((v) => v.id === targetVenueId)
                ? prev
                : [...prev, res.data]));
              setPinnedVenueId(targetVenueId);
            }
          } catch {
            // venue not found / unauthorized — leave a noop.
          }
        })();
      }
      return;
    }
    setExpandedVenueId(targetVenueId);
    venueListRef.current?.scrollToVenue(targetVenueId);

    if (searchParams.has('venue')) {
      searchParams.delete('venue');
      const qs = searchParams.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true, state: {} });
    } else {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    allVenues, location.pathname, location.search, location.state,
    navigate, sortedVenues, setAllVenues, setExpandedVenueId,
    setPinnedVenueId, venueListRef,
  ]);
}
